import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AscReportData } from '../../models/asc-report.model';
import { AscReportService, ReminderEmailRequest, ReminderMailResponse, SendReminderResponse } from '../../services/asc-report.service';
import { AscInfoComponent } from '../asc-info/asc-info';
import { AscCustomerComponent } from '../asc-customer/asc-customer';
import { AscTsinfoComponent } from '../asc-tsinfo/asc-tsinfo';
import { AscJobinfoComponent } from '../asc-jobinfo/asc-jobinfo';
import { AscAlertperiodComponent } from '../asc-alertperiod/asc-alertperiod';
import { AscTransferComponent } from '../asc-transfer/asc-transfer';
import { AscChgrequestComponent } from '../asc-chgrequest/asc-chgrequest';
import { AuthService } from '../../../services/auth.service';
import { PageTitleService } from '../../../services/pagetitle.service';
import { EncryptionService } from '../../../services/encryption.service';
import { Base64UrlService } from '../../../services/base64-url.service';
import { RepesentativeTimesheetComponent } from '../../../modules/admin/components/repesentative-timesheet/repesentative-timesheet.component';
import { RepResourceTimesheetComponent } from '../../../modules/admin/components/rep-resource-timesheet/rep-resource-timesheet.component';
import { RepApproverTimesheetComponent } from '../../../modules/admin/components/rep-approver-timesheet/rep-approver-timesheet.component';
import { AssociateTimeLogComponent } from '../../../modules/admin/components/associate-time-log/associate-time-log.component';

@Component({
  selector: 'app-asc-container',
  standalone: true,
  imports: [CommonModule, FormsModule, AscInfoComponent, AscCustomerComponent, AscTsinfoComponent, AscJobinfoComponent, AscAlertperiodComponent, AscTransferComponent, AscChgrequestComponent, RepesentativeTimesheetComponent, RepResourceTimesheetComponent, RepApproverTimesheetComponent, AssociateTimeLogComponent],
  providers: [Base64UrlService],
  templateUrl: './asc-container.html',
  styleUrl: './asc-container.scss',
})
export class AscContainerComponent implements OnInit {
  // TODO: テスト用にUserCodeを設定 - 本番環境では削除すること
  private testUserCode = '-3183';
  // タブ管理
  tabs: { id: string; name: string; minLevel?: number }[] = [];
  activeTab = 'asc-list';
  // 代理処理タブの状態
  //proxyOperation: 'return' | 'submit' | 'approve' | 'rep-view' | null = null;
  proxyOperation: 'return' | 'submit' | 'approve' | 'rep-view' | 'submit-log' | 'approve-log' | null = null;
  proxyEncryptedId: string = '';
  proxyEncryptedUserId: string = '';

  // 変更申請管理コンポーネントへの参照
  @ViewChild(AscChgrequestComponent) chgrequestComponent?: AscChgrequestComponent;

  // 年月度リスト（静的キャッシュ）
  private static yearMonthOptionsCache: { value: string; label: string }[] | null = null;
  yearMonthOptions: { value: string; label: string }[] = [];

  // フォームデータ
  formData = {
    yearMonth: '',
    closeDate: '', // '' (すべて) | '0' (末日) | '15' (15日)
    customerCode: '',
    branchNumbers: ''
  };

  // AND条件（ラジオボタン）
  andCondition = 'none'; // 'none', 'overtime', 'holiday', 'absence', 'paidHoliday', 'teleWork', 'unreported', 'unapproved', 'comments'

  // 表示項目（ラジオボタン）
  displayOption = 'allItems'; // 'allItems' | 'conditionItems'

  // 出力項目（ラジオボタン）
  outputOption = 'summary'; // 'summary' | 'detail'

  // テーブル描画に使用する確定値（ボタン次第一更新）
  appliedDisplayOption = 'allItems';
  appliedOutputOption = 'summary';

  // データ表示用
  reportData: AscReportData[] = [];
  displayColumns: string[] = [];
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  showRemindButton = false;
  readWrite = false;
  adminLevel = 0;

  // チェックボックス管理
  selectedItems: Set<string> = new Set();
  selectAll = false;

  // Web Worker用プロパティ
  showProgressBar = false;
  progressValue = 0;
  currentWorker: Worker | null = null;

  // ページング用プロパティ
  currentPage = 1;
  pageSize = 20; // 1ページあたりの件数（既定値を20に変更）
  totalRecords = 0;
  totalPages = 0;
  pageNumbers: number[] = []; // 表示するページ番号のリスト

  // 転記処理用プロパティ（子コンポーネントへ渡すため）
  selectedOdchgId: number | null = null;  // 転記対象のODchgID
  autoLoadRequestId: string | null = null;  // 自動ロードするRequestID

  // 顧客一覧ドロップダウン用プロパティ
  customerList: { customerID: number; customerName: string }[] = [];
  selectedCustomerId = '';
  isLoadingCustomerList = false;

  // 子コンポーネントへの参照
  @ViewChild(AscInfoComponent) ascInfoComponent?: AscInfoComponent;
  @ViewChild(AscTsinfoComponent) tsInfoComponent?: AscTsinfoComponent;
  @ViewChild(AscJobinfoComponent) jobOrderInfoComponent?: AscJobinfoComponent;
  @ViewChild(AscCustomerComponent) customerInfoComponent?: AscCustomerComponent;

  constructor(
    private ascReportService: AscReportService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private pageTitleService: PageTitleService,
    private en: EncryptionService,
    private base64Url: Base64UrlService
  ) {
    this.initializeYearMonthOptions();
    this.setDefaultValues();
  }

  ngOnInit(): void {
    console.log('🚀 asc-container component initialized');
    console.time('⏱️ Total component load');

    // TODO: テスト用にUserCodeが取得できない場合は-3183を設定 - 本番環境では削除すること
    const currentUserCode = this.authService.getCode();
    if (!currentUserCode) {
      this.authService.storeCode(this.testUserCode);
    }

    // ReadWriteを取得
    this.readWrite = this.authService.getReadWrite();
    this.adminLevel = this.authService.getAdminLevel() || 0;

    // コンポーネント初期化時にヘッダータイトルをクリア（前回の代理処理タイトルが残らないよう）
    this.pageTitleService.setTitle('');

    // タブを初期化
    this.initializeTabs();
    
    // トークンパラメータのチェック（外部リンク用）
    this.route.queryParams.subscribe(async params => {
      const token = params['token'];
      
      if (token) {
        // トークンベース（外部/メールリンク）
        await this.validateAndProcessToken(token);
      }
      console.timeEnd('⏱️ Total component load');
      console.log('✅ Component ready');
    });
  }

  // AdminLevelに基づいてタブを初期化
  private initializeTabs(): void {
    const adminLevel = this.authService.getAdminLevel();

    // 基本タブ(AdminLevel >= 1)
    this.tabs = [
      { id: 'asc-list', name: 'ASC一覧出力', minLevel: 1 },
      { id: 'timesheet-info', name: 'TS情報', minLevel: 1 },
      { id: 'job-order-info', name: '獲注情報', minLevel: 1 },
      { id: 'asc-info', name: 'ASC情報', minLevel: 1 },
      { id: 'customer-info', name: '顧客情報', minLevel: 1 },
    ];

    // 代理処理タブ (AdminLevel >= 3 かつ ReadWrite権限あり — スタッフ名リンクと同条件)
    if (adminLevel && adminLevel >= 3 && this.readWrite) {
      this.tabs.push({ id: 'proxy', name: '代理処理', minLevel: 3 });
    }

    // 転記処理タブ (AdminLevel >= 2)
    if (adminLevel && adminLevel >= 2) {
      this.tabs.push({ id: 'move_ts', name: '転記処理', minLevel: 2 });
    }

    // 追加タブ (AdminLevel = 4 or 9)
    if (adminLevel === 4 || adminLevel === 9) {
      this.tabs.push(
        { id: 'alert_etc', name: '変更申請管理', minLevel: 4 },
        { id: 'alert_period', name: 'アラート管理', minLevel: 4 }
      );
    }
  }

  // 内部遷移での直接アクセス処理（トークン不要）
  // 注: 転記処理タブは現在、アラート管理タブから直接起動する仕様から変更されました
  private async handleDirectAccess(odchgId: number, tabId: string): Promise<void> {
    /* コメントアウト: 後で機能として使うかも知れないため保持
    try {
      // AdminLevelチェック（転記処理タブは4または9のみ）
      const adminLevel = this.authService.getAdminLevel();
      if (tabId === 'move_ts' && adminLevel !== 4 && adminLevel !== 9) {
        console.error('Access denied: AdminLevel required for move_ts tab');
        alert('このタブにアクセスする権限がありません。');
        return;
      }

      // 転記処理タブを追加（内部遷移の場合も必要）
      if (tabId === 'move_ts') {
        const moveTabExists = this.tabs.some(tab => tab.id === 'move_ts');
        if (!moveTabExists) {
          this.tabs.push({ id: 'move_ts', name: '転記処理', minLevel: 4 });
        }
        
        // データIDを保存（転記処理コンポーネントが使用）
        sessionStorage.setItem('currentDataID', odchgId.toString());
        sessionStorage.setItem('currentResourceType', 'log.Log_OrderChange');
        sessionStorage.setItem('currentKeyColumn', 'ODchgID');
        
        // OdchgIDを保存（子コンポーネントに渡す）
        this.selectedOdchgId = odchgId;
        
        // タブをアクティブに
        this.activeTab = 'move_ts';
      } else {
        // その他のタブ処理（将来の拡張用）
        this.activeTab = tabId;
        sessionStorage.setItem('currentDataID', odchgId.toString());
      }
    } catch (error) {
      console.error('Error in direct access:', error);
      alert('データの読み込みに失敗しました。');
    }
    */
  }

  // トークンを検証して処理（汎用版・修正版）
  private async validateAndProcessToken(token: string): Promise<void> {
    try {
      const response = await this.ascReportService.validateToken(token).toPromise();
      
      if (response?.isValid) {
        // 通常タブを初期化
        this.initializeTabs();
        
        // 転記処理タブを追加（トークン経由の場合のみ）
        if (response.showMoveTs) {
          const moveTabExists = this.tabs.some(tab => tab.id === 'move_ts');
          if (!moveTabExists) {
            this.tabs.push({ id: 'move_ts', name: '転記処理', minLevel: 1 });
          }
          
          // 転記処理タブをアクティブに
          this.activeTab = 'move_ts';
          
          // データID、リソースタイプ、キーカラムを保存（後でデータ取得に使用）
          if (response.dataID && response.resourceType && response.keyColumn) {
            sessionStorage.setItem('currentDataID', response.dataID.toString());
            sessionStorage.setItem('currentResourceType', response.resourceType);
            sessionStorage.setItem('currentKeyColumn', response.keyColumn);
          }
        } else if (response.tabId) {
          // その他のタブIDが指定されている場合
          this.activeTab = response.tabId;
          
          // データID、リソースタイプ、キーカラムを保存
          if (response.dataID && response.resourceType && response.keyColumn) {
            sessionStorage.setItem('currentDataID', response.dataID.toString());
            sessionStorage.setItem('currentResourceType', response.resourceType);
            sessionStorage.setItem('currentKeyColumn', response.keyColumn);
          }
        }
      } else {
        console.error('Invalid token:', response?.errorMessage);
        alert('無効なリンクです。');
        this.initializeTabs();
      }
    } catch (error) {
      console.error('Token validation error:', error);
      alert('トークンの検証に失敗しました。');
      this.initializeTabs();
    }
  }

  // 年月度オプションを初期化（キャッシュを利用）
  initializeYearMonthOptions() {
    // 既にキャッシュがある場合はそれを使用
    if (AscContainerComponent.yearMonthOptionsCache) {
      this.yearMonthOptions = AscContainerComponent.yearMonthOptionsCache;
      return;
    }

    // 初回のみ生成してキャッシュに保存
    this.generateYearMonthOptions();
    AscContainerComponent.yearMonthOptionsCache = [...this.yearMonthOptions];
  }

  // 年月度オプションを生成（初回のみ実行）
  private generateYearMonthOptions() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // getMonth()は0-11なので+1
    const startYear = currentYear - 3;

    this.yearMonthOptions = [];

    // リスト先頭は現在月+1（翌月）から、過去3年分まで降順で生成
    let topYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    let topMonth = currentMonth === 12 ? 1 : currentMonth + 1;

    for (let year = topYear; year >= startYear; year--) {
      const startMonth = year === topYear ? topMonth : 12;
      const endMonth = year === startYear ? 1 : 1;

      for (let month = startMonth; month >= endMonth; month--) {
        const value = `${year}-${month.toString().padStart(2, '0')}`;
        const label = `${year}年${month}月`;
        this.yearMonthOptions.push({ value, label });
      }
    }
  }

  // タブ切替
  switchTab(tabId: string) {
    this.activeTab = tabId;
    // タブ移動時にヘッダーのタイトル文字をクリア
    this.pageTitleService.setTitle('');
    
    // 変更申請管理タブが選択された場合、初回ロードを実行
    if (tabId === 'alert_etc' && this.chgrequestComponent) {
      this.chgrequestComponent.onTabActivated();
    }

    // TS情報タブが選択された場合、登録番号またはASC UQが入力済みであれば自動検索
    if (tabId === 'timesheet-info' && this.tsInfoComponent) {
      if (this.tsInfoComponent.userCode || this.tsInfoComponent.ascUq) {
        setTimeout(() => this.tsInfoComponent!.onDisplay());
      }
    }
    
    // 転記処理タブは独立したタブとして動作（検索機能でデータ取得）
    // 直接アクセス可能なため、チェック不要
  }

  // 代理申請(submit)のBackボタンイベント → 代理処理タブ内でrepresentative-timesheet表示
  onRepResourceBack(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'rep-view';
    });
  }

  // 代理承認(approve)のBackボタンイベント → 代理処理タブ内でrepresentative-timesheet表示
  onRepApproverBack(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'rep-view';
    });
  }

  // representative-timesheet内の「スタッフ」ボタン → rep-resource-timesheetへ
  onRepViewToResource(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'submit';
    });
  }

  // representative-timesheet内の「承認者」ボタン → rep-approver-timesheetへ
  onRepViewToApprover(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'approve';
    });
  }

  // rep-resource-timesheet内の「履歴」ボタン → associate-time-logへ
  onRepResourceLog(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'submit-log';
    });
  }

  // rep-approver-timesheet内の「履歴」ボタン → associate-time-logへ
  onRepApproverLog(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'approve-log';
    });
  }

  // rep-resource-timesheet内の「戻る」ボタン → representative-timesheetへ
  onRepResourceLogBack(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'submit';
    });
  }

  // rep-approver-timesheet内の「戻る」ボタン → representative-timesheetへ
  onRepApproverLogBack(): void {
    this.proxyOperation = null;
    setTimeout(() => {
      this.proxyOperation = 'approve';
    });
  }

  // スタッフ名リンクから代理処理タブ（representative-timesheet）を開く
  navigateToRepView(timesheetId: string, userId: string): void {
    if (!timesheetId || !userId) return;
    this.proxyOperation = null;
    this.proxyEncryptedId = Base64UrlService.encode(this.en.encryptionAES(timesheetId.toString()));
    this.proxyEncryptedUserId = Base64UrlService.encode(this.en.encryptionAES(userId.toString()));
    setTimeout(() => {
      this.proxyOperation = 'rep-view';
      this.switchTab('proxy');
    });
  }

  // スタッフ名リンクから代理処理タブ（承認差戻）を開く
  navigateToApprovalReturn(timesheetId: string | number, userId: string | number): void {
    if (!timesheetId || !userId) return;
    this.proxyOperation = null;
    this.proxyEncryptedId = Base64UrlService.encode(this.en.encryptionAES(timesheetId.toString()));
    this.proxyEncryptedUserId = Base64UrlService.encode(this.en.encryptionAES(userId.toString()));
    setTimeout(() => {
      this.proxyOperation = 'return';
      this.switchTab('proxy');
    });
  }

  // 代理処理要求イベント（TS情報タブのボタンから）
  onProxyRequested(event: { operation: 'return' | 'submit' | 'approve'; encryptedId: string; encryptedUserId: string }): void {
    // 同じoperationで再要求された場合もコンポーネントが確実に再生成されるよう
    // 一度nullにして*ngIfでDOMを破棄させ、次のティックで新しい値をセットする
    this.proxyOperation = null;
    this.proxyEncryptedId = event.encryptedId;
    this.proxyEncryptedUserId = event.encryptedUserId;
    setTimeout(() => {
      this.proxyOperation = event.operation;
      this.switchTab('proxy');
    });
  }

  // 既定値を設定
  setDefaultValues() {
    const today = new Date();
    const currentDay = today.getDate();

    // 年月度：平日5日目＋月別オフセット以降なら当月、それより前なら先月
    this.formData.yearMonth = this.getDefaultYearMonth();

    // 顧客コード：空欄のまま
    this.formData.customerCode = '';

    // 支店番号：空欄のまま（既定は空欄）
    this.formData.branchNumbers = '';

    // 締日：本日が当月の11日～19日の場合は15日を既定で選択、それ以外は末日を既定で選択
    this.formData.closeDate = (currentDay >= 11 && currentDay <= 19) ? '15' : '0';

    // AND条件：なしを既定で選択
    this.andCondition = 'none';

    // 表示項目：全項目を既定で選択
    this.displayOption = 'allItems';

    // 出力項目：サマリーを既定で選択
    this.outputOption = 'summary';
  }

  // リセット処理
  resetForm() {
    const today = new Date();
    const currentDay = today.getDate();

    // 年月度：平日5日目＋月別オフセット以降なら当月、それより前なら先月
    this.formData.yearMonth = this.getDefaultYearMonth();

    // 顧客コード：空欄に戻す
    this.formData.customerCode = '';

    // 支店番号：空欄に戻す
    this.formData.branchNumbers = '';

    // 顧客一覧をリセット
    this.customerList = [];
    this.selectedCustomerId = '';

    // 締日：本日が当月の11日～19日の場合は15日を既定で選択、それ以外は末日を既定で選択
    this.formData.closeDate = (currentDay >= 11 && currentDay <= 19) ? '15' : '0';

    // AND条件：なしを既定で選択
    this.andCondition = 'none';

    // 表示項目：全項目を既定で選択
    this.displayOption = 'allItems';

    // 出力項目：サマリーを既定で選択
    this.outputOption = 'summary';

    // テーブル描画確定値もリセット
    this.appliedDisplayOption = 'allItems';
    this.appliedOutputOption = 'summary';

    // データをクリア
    this.reportData = [];
    this.errorMessage = '';
    this.showRemindButton = false;

    // ページング情報をリセット
    this.currentPage = 1;
    this.totalRecords = 0;
    this.totalPages = 0;
    this.pageNumbers = [];

    // チェックボックス状態をリセット
    this.selectedItems.clear();
    this.selectAll = false;
  }

  // 表示処理
  viewReport(page: number = 1, keepMessages: boolean = false) {
    // バリデーション
    if (!this.formData.yearMonth) {
      this.errorMessage = '年月度を選択してください。';
      return;
    }

    this.currentPage = page;
    this.isLoading = true;
    
    // keepMessagesがfalseの場合のみメッセージをクリア
    if (!keepMessages) {
      this.errorMessage = '';
      this.successMessage = '';
    }

    // リクエストオブジェクトを構築(ページング情報を含む)
    const request = {
      yearMonth: this.formData.yearMonth,
      closeDate: this.formData.closeDate,
      customerCode: this.formData.customerCode,
      branchNumbers: this.formData.branchNumbers,
      clientId: this.selectedCustomerId ? parseInt(this.selectedCustomerId, 10) : null,
      andCondition: this.andCondition,
      displayOption: this.displayOption,
      outputOption: this.outputOption,
      page: this.currentPage,
      pageSize: this.pageSize
    };

    // 確定値を更新（テーブル描画に使用）
    this.appliedDisplayOption = this.displayOption;
    this.appliedOutputOption = this.outputOption;

    // 表示カラムを決定
    this.displayColumns = this.ascReportService.getDisplayColumns(
      this.andCondition,
      this.displayOption,
      this.outputOption
    );

    // リマインド送信ボタンの表示/非表示
    //this.showRemindButton = (this.andCondition === 'unreported' || this.andCondition === 'unapproved');
    
    //一時的に承認者向け（unapproved）は無効化 2026-07-30
    // ASC向け（unreported）のみ送信可能
    this.showRemindButton = this.andCondition === 'unreported';

    console.time('⏱️ getAscReportData API');
    // API呼び出し
    this.ascReportService.getAscReportData(request).subscribe({
      next: (response) => {
        console.timeEnd('⏱️ getAscReportData API');
        console.log('📊 Raw ASC report data count:', response.data?.data?.length);
        console.time('⏱️ ASC Data Processing');
        
        // ResponseDtoの構造を考慮してデータを取得
        const responseData = response.data || response;
        this.reportData = responseData.data || [];
        this.totalRecords = responseData.totalCount || 0;
        this.totalPages = responseData.totalPages || Math.ceil(this.totalRecords / this.pageSize);
        this.currentPage = responseData.currentPage || this.currentPage;
        this.updatePageNumbers();
        console.timeEnd('⏱️ ASC Data Processing');
        console.log('📊 Processed ASC report count:', this.reportData.length);
        console.log('📊 Total records:', this.totalRecords, 'Total pages:', this.totalPages);
        this.isLoading = false;

        // チェックボックス状態を初期化
        this.initializeCheckboxStates();
        console.log('✅ ASC report ready');
      },
      error: (error) => {
        console.timeEnd('⏱️ getAscReportData API');
        this.errorMessage = error.error?.message || 'データの取得に失敗しました。しばらく時間をおいて再度お試しください。';
        this.isLoading = false;
        this.reportData = [];
        this.totalRecords = 0;
        this.totalPages = 0;
        this.pageNumbers = [];
      }
    });
  }

  // CSV出力処理（Web Worker使用）
  async exportCsv() {
    console.time('⏱️ Total CSV Export');
    // バリデーション
    if (!this.formData.yearMonth) {
      this.errorMessage = '年月度を選択してからCSV出力してください。';
      return;
    }

    // CSV出力時は常に全件再取得（画面はページング表示のため this.reportData は1ページ分のみ）
    this.isLoading = true;
    this.errorMessage = '';

    // CSV出力用のリクエストオブジェクトを構築（全件エクスポート専用エンドポイントを使用）
    const request = {
      yearMonth: this.formData.yearMonth,
      closeDate: this.formData.closeDate,
      customerCode: this.formData.customerCode,
      branchNumbers: this.formData.branchNumbers,
      clientId: this.selectedCustomerId ? parseInt(this.selectedCustomerId, 10) : null,
      andCondition: this.andCondition,
      displayOption: this.displayOption,
      outputOption: this.outputOption
    };

    // 確定値を更新（テーブル描画に使用）
    this.appliedDisplayOption = this.displayOption;
    this.appliedOutputOption = this.outputOption;

    // 表示カラムを決定
    this.displayColumns = this.ascReportService.getDisplayColumns(
      this.andCondition,
      this.displayOption,
      this.outputOption
    );

    try {
      console.time('⏱️ getAscReportData API (CSV)');
      // CSV全件エクスポート専用エンドポイントを使用（pageSize制限なし）
      const response = await this.ascReportService.exportAllAscReportData(request).toPromise();
      console.timeEnd('⏱️ getAscReportData API (CSV)');
      console.log('📊 CSV data fetched, count:', response?.data?.data?.length);
      
      // ResponseDtoの構造を考慮してデータを取得
      const responseData = response?.data || response;
      this.reportData = responseData?.data || [];
      this.isLoading = false;

      // チェックボックス状態をリセット
      this.selectedItems.clear();
      this.selectAll = false;

    } catch (error) {
      console.timeEnd('⏱️ getAscReportData API (CSV)');
      console.timeEnd('⏱️ Total CSV Export');
      this.errorMessage = 'データの取得に失敗しました。';
      this.isLoading = false;
      return;
    }

    // データ取得後もデータがない場合は終了
    if (this.reportData.length === 0) {
      this.errorMessage = 'CSV出力対象のデータがありません。';
      return;
    }

    // CSV生成処理（同期処理に変更）
    this.isLoading = true;

    try {
      // CSVヘッダーを構築
      const headerMap: { [key: string]: string } = {
        'customerName': '顧客名',
        'jobOrderId': '獲注ID',
        'jobOrderLink': '獲注ID',
        'timesheetId': 'タイムシートID',
        'timesheetLink': 'タイムシートID',
        'customerOfficeId': '顧客部署ID',
        'customerOfficeName': '顧客部署名',
        'customerLink': '顧客部署ID',
        'branchId': '支店',
        'ascUq': 'ASC UQ',
        'ascLink': 'ASC UQ',
        'staffCode': 'ASC登録番号',
        'staffName': 'ASC氏名',
        'userId': 'ASC UQ',
        'userCode': 'ASC登録番号',
        'userName': 'ASC氏名',
        'workTime': '時間内',
        'overWorkTime': '時間外',
        'midnightInWorkTime': '深夜内',
        'midnightOverWorkTime': '深夜外',
        'total': '合計',
        'overtimeDays': '残業日',
        'holidayWork': '休日勤務',
        'absence': '欠勤',
        'paidHoliday': '有休',
        'teleWork': '在宅',
        'editing': '未報告',
        'submitted': '報告済',
        'denied': '否認',
        'approved': '承認済',
        'attendance': '出勤',
        'ascRemind': '最終リマインド日時',
        'cusRemind': '最終リマインド日時',
        'comments': 'コメント',
        'dayDate': '対象日'
      };

      // 最適化: チェックボックス列およびCSV不要列を除外したカラムリストを事前に作成
      const csvExcludeColumns = new Set(['checkbox', 'timesheetId', 'timesheetLink', 'customerOfficeId', 'customerLink', 'ascUq', 'ascLink']);
      const rawCsvColumns = this.displayColumns.filter(col => !csvExcludeColumns.has(col));
      // 獲注ID列（jobOrderId または jobOrderLink）を先頭に移動
      const jobOrderCol = rawCsvColumns.find(c => c === 'jobOrderId' || c === 'jobOrderLink');
      const csvColumns = jobOrderCol
        ? [jobOrderCol, ...rawCsvColumns.filter(c => c !== jobOrderCol)]
        : rawCsvColumns;
      
      // ヘッダー行を作成
      const headers = csvColumns.map(col => headerMap[col] || col);
      
      const csvLines = [headers.join(',')];

      // データ行を生成（事前にフィルタリングしたcsvColumnsを使用）
      for (const row of this.reportData) {
        const csvRow = csvColumns.map(col => this.getCsvValue(row, col));
        csvLines.push(csvRow.join(','));
      }

      // CSV文字列を生成
      const csvContent = csvLines.join('\n');

      // UTF-8 BOM付きでダウンロード
      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], {
        type: 'text/csv;charset=utf-8;'
      });

      const fileName = this.generateCsvFileName();
      this.downloadBlob(blob, fileName);

      console.timeEnd('⏱️ Total CSV Export');
      console.log('✅ CSV export completed:', fileName);
      this.isLoading = false;

    } catch (error) {
      console.timeEnd('⏱️ Total CSV Export');
      this.errorMessage = 'CSV出力に失敗しました。';
      this.isLoading = false;
    }
  }

  // CSV出力をキャンセル
  cancelCsvExport() {
    if (this.currentWorker) {
      this.currentWorker.terminate();
      this.currentWorker = null;
    }
    this.showProgressBar = false;
    this.isLoading = false;
    this.progressValue = 0;
  }

  // 小数時間をHH:MM形式に変換（例: 39.95 → "39:57"）
  // Math.round(decimal * 60) で分を正確に復元してからフォーマット
  toHHMM(decimalHours: number | null | undefined): string {
    if (decimalHours == null) return '';
    const totalMinutes = Math.round(decimalHours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
  }

  // デフォルト年月を返す（平日5日目＋月別オフセット以降なら当月、それより前は先月）YYYY-MM形式
  private getDefaultYearMonth(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based
    const day = today.getDate();

    // 月ごとの加算日数（0-based月インデックス）
    const monthOffset: { [key: number]: number } = {
      0:  3,  // 1月 +3
      4:  3,  // 5月 +3
      10: 1,  // 11月 +1
    };
    const offset = monthOffset[month] ?? 0;

    // その月の平日5日目を求める
    let weekdayCount = 0;
    let fifthWeekdayDate = 0;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(year, month, d).getDay();
      if (dow !== 0 && dow !== 6) {
        weekdayCount++;
        if (weekdayCount === 5) {
          fifthWeekdayDate = d;
          break;
        }
      }
    }

    // 閾値 = 平日5日目 + オフセット。閾値以降なら当月、それより前なら先月
    const threshold = fifthWeekdayDate + offset;
    if (day >= threshold) {
      return `${year}-${(month + 1).toString().padStart(2, '0')}`;
    } else {
      const prevMonth = month === 0 ? 12 : month;
      const prevYear  = month === 0 ? year - 1 : year;
      return `${prevYear}-${prevMonth.toString().padStart(2, '0')}`;
    }
  }

  // CSVヘッダーを構築
  private buildCsvHeaders(): string {
    const headerMap: { [key: string]: string } = {
      'checkbox': '',
      'jobOrderId': '獲注ID',
      'timesheetId': 'タイムシートID',
      'customerOfficeId': '顧客部署ID',
      'branchId': '支店',
      'userId': 'ASC UQ',
      'userCode': 'ASC登録番号',
      'userName': 'ASC氏名',
      'workTime': '時間内',
      'overWorkTime': '時間外',
      'midnightInWorkTime': '深夜内',
      'midnightOverWorkTime': '深夜外',
      'total': '合計',
      'overtimeDays': '残業日',
      'holidayWork': '休日勤務',
      'absence': '欠勤',
      'paidHoliday': '有休',
      'teleWork': '在宅',
      'editing': '未報告',
      'submitted': '報告済',
      'denied': '否認',
      'approved': '承認済',
      'ascRemind': 'ASCリマインド日',
      'cusRemind': '顧客リマインド日',
      'comments': 'コメント'
    };

    return this.displayColumns
      .filter(col => col !== 'checkbox') // チェックボックス列は除外
      .map(col => this.escapeCsvValue(headerMap[col] || col))
      .join(',');
  }

  // CSVデータ行を構築
  private buildCsvRow(row: AscReportData): string {
    const values: string[] = [];

    this.displayColumns.forEach(column => {
      if (column === 'checkbox') return; // チェックボックス列は除外

      let value = '';
      switch (column) {
        case 'jobOrderId':
          value = row.jobOrderId?.toString() || '';
          break;
        case 'timesheetId':
          value = row.timesheetId || '';
          break;
        case 'customerOfficeId':
          value = row.customerOfficeId?.toString() || '';
          break;
        case 'customerOfficeName':
          value = row.customerOfficeName || '';
          break;
        case 'branchId':
          value = row.branchNumber ? (row.branchNumber + '').padStart(2, '0') : '';
          break;
        case 'ascUq':
          value = row.ascUq?.toString() || '';
          break;
        case 'staffCode':
          value = row.staffCode || '';
          break;
        case 'staffName':
          value = row.staffName || '';
          break;
        case 'userId':
          value = row.userId || '';
          break;
        case 'userCode':
          value = row.userCode || '';
          break;
        case 'userName':
          value = row.userName || '';
          break;
        case 'workTime':
          value = row.workTime?.toFixed(2) || '0.00';
          break;
        case 'overWorkTime':
          value = row.overWorkTime?.toFixed(2) || '0.00';
          break;
        case 'midnightInWorkTime':
          value = row.midnightInWorkTime?.toFixed(2) || '0.00';
          break;
        case 'midnightOverWorkTime':
          value = row.midnightOverWorkTime?.toFixed(2) || '0.00';
          break;
        case 'total':
          value = row.total?.toFixed(2) || '0.00';
          break;
        case 'overtimeDays':
          value = row.overtimeDays?.toString() || '0';
          break;
        case 'holidayWork':
          value = row.holidayWork?.toString() || '0';
          break;
        case 'absence':
          value = row.absence?.toString() || '0';
          break;
        case 'paidHoliday':
          value = row.paidHoliday?.toString() || '0';
          break;
        case 'teleWork':
          value = row.teleWork?.toString() || '0';
          break;
        case 'editing':
          value = row.editing?.toString() || '0';
          break;
        case 'submitted':
          value = row.submitted?.toString() || '0';
          break;
        case 'denied':
          value = row.denied?.toString() || '0';
          break;
        case 'approved':
          value = row.approved?.toString() || '0';
          break;
        case 'ascRemind':
          value = row.ascRemind ? 'はい' : 'いいえ';
          break;
        case 'cusRemind':
          value = row.cusRemind ? 'はい' : 'いいえ';
          break;
        case 'comments':
          value = row.comments || '';
          break;
        default:
          value = '';
      }

      values.push(this.escapeCsvValue(value));
    });

    return values.join(',');
  }

  // CSV値をエスケープ
  private escapeCsvValue(value: string): string {
    if (!value) return '';

    // カンマ、改行、ダブルクォートが含まれている場合はダブルクォートで囲む
    if (value.includes(',') || value.includes('\n') || value.includes('\r') || value.includes('"')) {
      // ダブルクォートをエスケープ（""に変換）
      const escapedValue = value.replace(/"/g, '""');
      return `"${escapedValue}"`;
    }

    return value;
  }

  // 日時フォーマット（CSV用）
  private formatDateTimeForCsv(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  // CSV値を取得してエスケープ
  private getCsvValue(row: AscReportData, col: string): string {
    let value = '';
    
    switch(col) {
      case 'customerName':
        value = row.customerName || '';
        break;
      case 'jobOrderId':
      case 'jobOrderLink':
        value = row.jobOrderId?.toString() || '';
        break;
      case 'timesheetId':
      case 'timesheetLink':
        value = row.timesheetId?.toString() || '';
        break;
      case 'customerOfficeId':
      case 'customerLink':
        value = row.customerOfficeId?.toString() || '';
        break;
      case 'customerOfficeName':
        value = row.customerOfficeName || '';
        break;
      case 'branchId':
        value = row.branchNumber || '';
        break;
      case 'ascUq':
      case 'ascLink':
        value = row.ascUq?.toString() || '';
        break;
      case 'staffCode':
        value = row.staffCode || '';
        break;
      case 'staffName':
        value = row.staffName || '';
        break;
      case 'userId':
        value = row.ascUq?.toString() || '';
        break;
      case 'userCode':
        value = row.staffCode || '';
        break;
      case 'userName':
        value = row.staffName || '';
        break;
      case 'workTime':
        value = row.workTime != null ? row.workTime.toFixed(2) : '';
        break;
      case 'overWorkTime':
        value = row.overWorkTime != null ? row.overWorkTime.toFixed(2) : '';
        break;
      case 'midnightInWorkTime':
        value = row.midnightInWorkTime != null ? row.midnightInWorkTime.toFixed(2) : '';
        break;
      case 'midnightOverWorkTime':
        value = row.midnightOverWorkTime != null ? row.midnightOverWorkTime.toFixed(2) : '';
        break;
      case 'total':
        value = row.total != null ? row.total.toFixed(2) : '';
        break;
      case 'overtimeDays':
        value = row.overtimeDays?.toString() || '';
        break;
      case 'holidayWork':
        value = row.holidayWork?.toString() || '';
        break;
      case 'absence':
        value = row.absence?.toString() || '';
        break;
      case 'paidHoliday':
        value = row.paidHoliday?.toString() || '';
        break;
      case 'teleWork':
        value = row.teleWork?.toString() || '';
        break;
      case 'editing':
        value = row.editing?.toString() || '';
        break;
      case 'submitted':
        value = row.submitted?.toString() || '';
        break;
      case 'denied':
        value = row.denied?.toString() || '';
        break;
      case 'approved':
        value = row.approved?.toString() || '';
        break;
      case 'attendance':
        value = (row.attendanceDays ?? 0).toString();
        break;
      case 'ascRemind':
        value = row.ascRemind ? this.formatDateTimeForCsv(row.ascRemind) : '';
        break;
      case 'cusRemind':
        value = row.cusRemind ? this.formatDateTimeForCsv(row.cusRemind) : '';
        break;
      case 'comments':
        value = row.comments || '';
        break;
      case 'dayDate':
        value = row.dayDate ? new Date(row.dayDate).toLocaleDateString('ja-JP') : '';
        break;
      default:
        value = '';
    }
    
    // CSVエスケープ処理
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  }

  // CSVファイル名を生成
  private generateCsvFileName(): string {
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/[:\-T]/g, '');

    let fileName = 'ASC一覧';

    if (this.formData.yearMonth) {
      fileName += `_${this.formData.yearMonth}`;
    }

    if (this.andCondition !== 'none') {
      const conditionNames: { [key: string]: string } = {
        'overtime': '残業日あり',
        'holiday': '休日勤務あり',
        'absence': '欠勤日あり',
        'paidHoliday': '有休取得あり',
        'teleWork': '在宅勤務あり',
        'unreported': '未報告否認あり',
        'unapproved': '未承認あり',
        'comments': 'コメントあり'
      };
      fileName += `_${conditionNames[this.andCondition] || this.andCondition}`;
    }

    fileName += `_${timestamp}.csv`;

    return fileName;
  }

  // Blobをダウンロード
  private downloadBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // メモリリークを防ぐためURLを解放
    window.URL.revokeObjectURL(url);
  }

  // AND条件変更時の処理
  onAndConditionChange() {
    // 「なし」が選択された場合は表示項目を「全項目」に設定
    if (this.andCondition === 'none') {
      this.displayOption = 'allItems';
      this.outputOption = 'summary';
    }
    // 表示項目・出力項目の選択肢を更新
    this.updateAvailableOptions();
  }

  // 利用可能な選択肢を更新
  updateAvailableOptions() {
    // AND条件に基づいて表示項目と出力項目の選択肢を制御
    if (this.andCondition === 'none') {
      // なしの場合：表示項目は「全項目」のみ、出力項目は「サマリー」のみ
      this.displayOption = 'allItems';
      this.outputOption = 'summary';
    } else if (this.andCondition === 'unreported' || this.andCondition === 'unapproved' || this.andCondition === 'comments') {
      // 未報告/否認あり、未承認あり、コメントありの場合：表示項目は「条件項目」のみ、出力項目は「サマリー」のみ
      this.displayOption = 'conditionItems';
      this.outputOption = 'summary';
    } else if (this.andCondition === 'overtime' || this.andCondition === 'holiday') {
      // 残業日あり、休日勤務ありの場合：詳細モード不可
      if (this.outputOption === 'detail') {
        this.outputOption = 'summary';
      }
    }
    // 欠勤日あり、有休取得あり、在宅勤務ありの場合は詳細モード選択可能（変更なし）
  }

  // リマインド送信処理
  sendReminder() {

    // 一時的に承認者向けリマインドを無効化 2026-07-30
    if (this.andCondition === 'unapproved') {
      this.errorMessage =
        '承認者向けリマインドメールは現在一時的に停止しています。';
      return;
    }

    // 期間チェック
    if (!this.isValidReminderPeriod()) {
      this.errorMessage = '過去分へのリマインド送信はできません。';
      return;
    }

    if (this.selectedItems.size === 0) {
      this.errorMessage = 'リマインドを送信する対象を選択してください。';
      return;
    }

    const selectedTimesheetIds = Array.from(this.selectedItems);

    // 選択されたアイテムから必要な情報を収集
    const selectedData = this.reportData.filter(item =>
      this.selectedItems.has(item.timesheetId)
    );

    const request: ReminderEmailRequest = {
      yearMonth: this.formData.yearMonth.replace('-', ''),  // "2025-12" → "202512" に変換
      closingDay: this.formData.closeDate,  // '' (すべて) | '0' (末日) | '15' (15日)
      andCondition: this.andCondition,
      timesheetIds: selectedTimesheetIds.map(id => id.toString()),
      jobOrderIds: [...new Set(selectedData.map(item => item.jobOrderId?.toString()).filter((id): id is string => id !== undefined))],
      userIds: [...new Set(selectedData.map(item => item.ascUq?.toString()).filter((id): id is string => id !== undefined))]
    };


    // /reminder-mail-info で対象確認とメール本文プレビューを一括取得
    // （/reminder-emails の二重呼び出しを解消）
    this.ascReportService.getReminderMailInfo(request).subscribe({
      next: (mailResponse) => {
        if (!mailResponse.data || mailResponse.data.mailInfos.length === 0) {
          this.errorMessage = '送信対象のメールアドレスが見つかりませんでした。';
          return;
        }
        this.displayReminderMailInfo(mailResponse.data, this.selectedItems.size);
      },
      error: (error) => {
        this.errorMessage = 'リマインドメール情報の取得に失敗しました。';
      }
    });
  }

  // リマインド送信可能な期間かどうかをチェック
  isValidReminderPeriod(): boolean {
    if (!this.formData.yearMonth) {
      return false;
    }

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // getMonth()は0-11なので+1

    // 選択された年月を解析（YYYY-MM形式）
    const [selectedYearStr, selectedMonthStr] = this.formData.yearMonth.split('-');
    const selectedYear = parseInt(selectedYearStr);
    const selectedMonth = parseInt(selectedMonthStr);

    // 現在年月の前月を計算
    let lastMonthYear = currentYear;
    let lastMonth = currentMonth - 1;
    if (lastMonth === 0) {
      lastMonth = 12;
      lastMonthYear--;
    }

    // 選択年月が当月または先月の場合はOK
    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return true; // 当月
    } else if (selectedYear === lastMonthYear && selectedMonth === lastMonth) {
      return true; // 先月
    } else {
      return false; // 過去分
    }
  }

  // 年月度キャッシュをクリア（必要時に使用）
  static clearYearMonthCache() {
    AscContainerComponent.yearMonthOptionsCache = null;
  }

  // 全選択チェックボックスの状態変更
  onSelectAllChange() {
    if (this.selectAll) {
      // 全選択：すべてのアイテムを選択状態にする
      this.reportData.forEach(item => {
        this.selectedItems.add(item.timesheetId);
      });
    } else {
      // 全解除：すべての選択を解除
      this.selectedItems.clear();
    }
  }

  // 個別チェックボックスの状態変更
  onItemSelectionChange(timesheetId: string, isChecked: boolean) {
    if (isChecked) {
      this.selectedItems.add(timesheetId);
    } else {
      this.selectedItems.delete(timesheetId);
    }

    // 全選択チェックボックスの状態を更新
    this.updateSelectAllState();
  }

  // 全選択チェックボックスの状態を更新
  updateSelectAllState() {
    const totalItems = this.reportData.length;
    const selectedCount = this.selectedItems.size;

    if (selectedCount === 0) {
      this.selectAll = false;
    } else if (selectedCount === totalItems) {
      this.selectAll = true;
    } else {
      // 部分選択の場合は、全選択チェックボックスをfalseにする
      this.selectAll = false;
    }
  }

  // アイテムが選択されているかどうかを確認
  isItemSelected(timesheetId: string): boolean {
    return this.selectedItems.has(timesheetId);
  }

  // 選択されたアイテムのIDリストを取得
  getSelectedItems(): string[] {
    return Array.from(this.selectedItems);
  }

  // ページング関連メソッド

  // ページ番号リストを更新
  updatePageNumbers() {
    this.pageNumbers = [];
    const maxPageButtons = 5; // 表示するページボタンの最大数

    let start = Math.max(1, this.currentPage - Math.floor(maxPageButtons / 2));
    let end = Math.min(this.totalPages, start + maxPageButtons - 1);

    // 表示範囲を調整
    if (end - start + 1 < maxPageButtons) {
      start = Math.max(1, end - maxPageButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      this.pageNumbers.push(i);
    }
  }

  // 前のページに移動
  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.viewReport(this.currentPage - 1);
    }
  }

  // 次のページに移動
  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.viewReport(this.currentPage + 1);
    }
  }

  // 指定したページに移動
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.viewReport(page);
    }
  }

  // 最初のページに移動
  goToFirstPage() {
    if (this.currentPage !== 1) {
      this.viewReport(1);
    }
  }

  // 最後のページに移動
  goToLastPage() {
    if (this.currentPage !== this.totalPages) {
      this.viewReport(this.totalPages);
    }
  }

  // ページサイズを変更
  changePageSize(newPageSize: number) {
    this.pageSize = newPageSize;
    this.viewReport(1); // 1ページ目から表示し直し
  }

  // 現在の表示範囲を取得（例：「1-100件表示中」）
  getDisplayRange(): string {
    if (this.totalRecords === 0) return '0件';

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);

    return `${start.toLocaleString()}-${end.toLocaleString()}件表示中`;
  }

  // ===== 顧客一覧 =====

  loadCustomerList() {
    if (!this.formData.yearMonth) {
      this.errorMessage = '年月度を選択してください。';
      return;
    }
    this.isLoadingCustomerList = true;
    this.customerList = [];
    this.selectedCustomerId = '';

    this.ascReportService.getCustomerList(this.formData.yearMonth, this.formData.branchNumbers).subscribe({
      next: (response) => {
        this.customerList = response?.data ?? [];
        this.isLoadingCustomerList = false;
        if (this.customerList.length === 0) {
          this.errorMessage = '対象の顧客が見つかりませんでした。';
        }
      },
      error: (err) => {
        console.error('顧客一覧取得エラー:', err);
        this.errorMessage = '顧客一覧の取得に失敗しました。';
        this.isLoadingCustomerList = false;
      }
    });
  }

  // 顧客一覧から顧客が選択されたときの処理
  onCustomerSelect() {
    if (this.selectedCustomerId) {
      // 顧客一覧で選択したら顧客コードテキストボックスをクリア
      this.formData.customerCode = '';
    }
  }

  // 顧客コードの入力制御（XXX-XXXX形式）
  validateCustomerCodeInput(event: KeyboardEvent): boolean {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    // 英数字・ハイフン・半角スペースのみ許可
    const pattern = /^[0-9A-Za-z \-]$/;
    if (!pattern.test(event.key)) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  // 顧客コードの自動フォーマット（XXX-XXXX または XXX- XXXX 形式）
  // ハイフン直後のスペースを保持する（例: "555- 555"）
  formatCustomerCode(event: any) {
    if ((event as InputEvent).isComposing) return;
    // 顧客コードが入力されたら顧客一覧の選択をクリア
    this.selectedCustomerId = '';

    const rawValue = event.target.value;

    // 英数字・ハイフン・半角スペース以外を削除
    const cleaned = rawValue.replace(/[^0-9A-Za-z \-]/g, '');

    // ハイフンの位置を検出
    const hyphenIndex = cleaned.indexOf('-');

    if (hyphenIndex === -1) {
      // 先頭から英数字3文字を抽出し、その後ろを確認する
      const beforeMatch = cleaned.match(/^([0-9A-Za-z]{0,3})(.*)/s);
      const before = beforeMatch ? beforeMatch[1] : '';
      const rest = beforeMatch ? beforeMatch[2] : '';

      if (before.length < 3 || rest.length === 0) {
        // 3文字未満、またはそれ以降に何もない（入力途中）
        this.formData.customerCode = before;
        return;
      }

      // 3文字の直後がスペース → ハイフン+スペース形式（例: "123 5" → "123- 5"）
      if (rest[0] === ' ') {
        const afterAlphaNum = rest.replace(/[^0-9A-Za-z]/g, '').substring(0, 3);
        this.formData.customerCode = before + '-' + (afterAlphaNum.length > 0 ? ' ' + afterAlphaNum : ' ');
        return;
      }

      // 4文字目が英数字 → 自動でハイフンを挿入
      const afterAlphaNum = rest.replace(/[^0-9A-Za-z]/g, '').substring(0, 4);
      this.formData.customerCode = before + '-' + afterAlphaNum;
      return;
    }

    // ハイフン前: 英数字のみ最大3文字
    const before = cleaned.substring(0, hyphenIndex).replace(/[^0-9A-Za-z]/g, '').substring(0, 3);

    // ハイフン後の文字列
    const afterRaw = cleaned.substring(hyphenIndex + 1);

    // ハイフン直後がスペースか判定
    const hasSpaceAfterHyphen = afterRaw.length > 0 && afterRaw[0] === ' ';

    // 英数字のみ抽出（スペースあり → 最大3文字、スペースなし → 最大4文字）
    const afterAlphaNum = afterRaw.replace(/[^0-9A-Za-z]/g, '').substring(0, hasSpaceAfterHyphen ? 3 : 4);

    let formatted = before + '-';
    if (hasSpaceAfterHyphen) {
      formatted += ' ' + afterAlphaNum;
    } else {
      formatted += afterAlphaNum;
    }

    this.formData.customerCode = formatted;
  }

  // 抽出条件のサマリーを取得
  getConditionSummary(): string {
    const conditions: string[] = [];

    // 年月度
    if (this.formData.yearMonth) {
      const [year, month] = this.formData.yearMonth.split('-');
      conditions.push(`${year}年${parseInt(month)}月度`);
    }

    // 締日
    if (this.formData.closeDate !== '') {
      const closeDateNames: { [key: string]: string } = {
        '15': '15日締',
        '0': '月末締'
      };
      conditions.push(closeDateNames[this.formData.closeDate] || this.formData.closeDate);
    }

    // 支店番号
    if (this.formData.branchNumbers && this.formData.branchNumbers.trim()) {
      const branchCount = this.formData.branchNumbers.split(',').filter(b => b.trim()).length;
      if (branchCount === 1) {
        conditions.push(`支店${this.formData.branchNumbers.trim()}`);
      } else {
        conditions.push(`${branchCount}支店指定`);
      }
    }

    // AND条件
    if (this.andCondition !== 'none') {
      const conditionNames = {
        'overtime': '残業日あり',
        'holiday': '休日勤務あり',
        'absence': '欠勤日あり',
        'paidHoliday': '有休取得あり',
        'teleWork': '在宅勤務あり',
        'unreported': '未報告/否認あり',
        'unapproved': '未承認あり',
        'comments': 'コメントあり'
      };
      conditions.push(conditionNames[this.andCondition as keyof typeof conditionNames] || this.andCondition);
    }

    return conditions.join('、');
  }

  // チェックボックス状態を初期化
  initializeCheckboxStates() {
    this.selectedItems.clear();
    
    // 初期状態ではすべて未選択にする（誤送信防止）
    this.selectAll = false;
    
    // 以下のコードをコメントアウト（自動全選択を無効化）
    // リマインド対象条件（未報告/否認あり、未承認あり）の場合はすべてONに設定
    // if (this.andCondition === 'unreported' || this.andCondition === 'unapproved') {
    //   // 現在のページのすべてのアイテムを選択状態にする
    //   this.reportData.forEach(item => {
    //     this.selectedItems.add(item.timesheetId);
    //   });
    //   this.selectAll = true;
    // } else {
    //   // その他の条件ではすべてOFFに設定
    //   this.selectAll = false;
    // }
  }

  // リマインドメール情報を表示
  displayReminderMailInfo(mailResponse: ReminderMailResponse, selectedCount: number) {
    if (mailResponse.mailInfos.length === 0) {
      this.errorMessage = '送信対象のメール情報が見つかりませんでした。';
      return;
    }

    // AND条件に応じてメッセージを決定（選択したタイムシート件数を表示）
    const confirmMessage = this.andCondition === 'unapproved'
      ? `${selectedCount}件の承認者向けリマインドメールを送信します。よろしいですか？`
      : `${selectedCount}件のリマインドメールを送信します。よろしいですか？`;

    const confirmed = confirm(confirmMessage);

    if (confirmed) {
      // 実際のメール送信処理を呼び出す
      this.sendActualReminderMails(mailResponse);
    }
  }

  // 実際のメール送信処理
  private sendActualReminderMails(mailResponse: ReminderMailResponse) {
    // 元のリクエスト情報を再構築
    const selectedTimesheetIds = Array.from(this.selectedItems);
    const selectedJobOrderIds = this.reportData
      .filter(item => this.selectedItems.has(item.timesheetId))
      .map(item => item.jobOrderId?.toString())
      .filter((id): id is string => id !== undefined);
    const selectedUserIds = this.reportData
      .filter(item => this.selectedItems.has(item.timesheetId))
      .map(item => item.ascUq?.toString())
      .filter((id): id is string => id !== undefined);

    const request: ReminderEmailRequest = {
      yearMonth: this.formData.yearMonth.replace('-', ''),  // "2025-12" → "202512" に変換
      closingDay: this.formData.closeDate,  // '' (すべて) | '0' (末日) | '15' (15日)
      andCondition: this.andCondition,
      timesheetIds: selectedTimesheetIds.map(id => id.toString()),
      jobOrderIds: selectedJobOrderIds,
      userIds: selectedUserIds,
      mailInfos: mailResponse.mailInfos  // 取得済みの情報を渡してバックエンドでの再取得をスキップ
    };

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.ascReportService.sendReminderMails(request).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response.data.errors.length > 0) {
          this.errorMessage = `${response.data.sentCount}件送信完了、${response.data.errors.length}件エラー\n\nエラー:\n${response.data.errors.join('\n')}`;
        } else {
          this.successMessage = `${selectedTimesheetIds.length}件のリマインドメールを正常に送信しました。`; // 送信対象件数を表示
        }
        
        // 送信後、現在の設定で一覧を再取得して表示を更新（メッセージは保持）
        this.viewReport(1, true);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'リマインドメールの送信に失敗しました。';
      }
    });
  }

  // タブ間ナビゲーション用メソッド
  navigateToJobOrderInfo(jobOrderId: number) {
    this.switchTab('job-order-info');
    setTimeout(() => {
      if (this.jobOrderInfoComponent) {
        this.jobOrderInfoComponent.loadJobOrderInfo(jobOrderId);
      }
    }, 100);
  }

  navigateToTsInfo(params: { ascUq?: string; userCode?: string; yearMonth?: string; timesheetId?: number }) {
    // タブ切替前にTsInfoの状態をリセット（switchTab内のonDisplay自動実行による二重リクエストを防止）
    if (this.tsInfoComponent) {
      this.tsInfoComponent.userCode = '';
      this.tsInfoComponent.ascUq = '';
    }
    this.switchTab('timesheet-info');
    setTimeout(() => {
      if (this.tsInfoComponent) {
        if (params.ascUq) {
          this.tsInfoComponent.loadByAscUq(params.ascUq, params.userCode, params.yearMonth, params.timesheetId);
        } else if (params.timesheetId) {
          this.tsInfoComponent.loadByTimesheetId(params.timesheetId, params.yearMonth, params.userCode);
        }
      }
    }, 100);
  }

  navigateToAscInfo(params: { userCode?: string; ascUq?: string }) {
    this.switchTab('asc-info');
    setTimeout(() => {
      if (this.ascInfoComponent) {
        if (params.userCode) {
          this.ascInfoComponent.loadByUserCode(params.userCode);
        } else if (params.ascUq) {
          this.ascInfoComponent.loadByAscUq(params.ascUq);
        }
      }
    }, 100);
  }

  navigateToCustomerInfo(customerOfficeId: number) {
    this.switchTab('customer-info');
    setTimeout(() => {
      if (this.customerInfoComponent) {
        this.customerInfoComponent.loadByCustomerOfficeId(customerOfficeId);
      }
    }, 100);
  }

  // ============================
  // 子コンポーネント連携メソッド
  // ============================
  
  /**
   * アラート期間タブから転記処理タブへ遷移（子コンポーネントからのイベント受信）
   */
  onTransferToProcessing(alert: any) {
    // OdchgIDを保存
    this.selectedOdchgId = alert.oDchgID;
    // タブ切替（switchTab 経由ではないためタイトルを明示クリア）
    this.pageTitleService.setTitle('');
    this.activeTab = 'move_ts';
  }

  /**
   * 変更申請管理タブから転記処理タブへ遷移（RequestIDで）
   */
  navigateToTransferWithRequestId(requestId: string) {
    // RequestIDを保存
    this.autoLoadRequestId = requestId;
    // OdchgIDをクリア（RequestIDで検索するため）
    this.selectedOdchgId = null;
    // タブ切替（switchTab 経由ではないためタイトルを明示クリア）
    this.pageTitleService.setTitle('');
    this.activeTab = 'move_ts';
  }
}